import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import i18n from '../../i18n';
import { formatApiErrors, hasError } from '../../utils/formatError';

/**
 * Sends a message through a Telegram user session.
 *
 * Mostly here to open a chat: a bank's bot only shows up as a linkable chat once
 * the account has spoken to it, so the operator sends it /start from here and
 * then picks the chat on the gateway.
 *
 * The peer stays a string all the way to the API — numeric Telegram ids exceed
 * 2^53 and would be rounded as a JS number.
 */
export const TgSessionSendDialog = ({ session, openDialog, closeDialog, server }) => {
    const [peer, setPeer] = useState('');
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sent, setSent] = useState(null);

    const canSend = peer.trim() !== '' && text.trim() !== '' && !loading;

    const send = async () => {
        if (!canSend) return;
        setLoading(true);
        setError(null);
        setSent(null);
        try {
            const result = await server.send(session.id, peer.trim(), text);
            if (hasError(result)) {
                setError(formatApiErrors(result).join('\n'));
            } else {
                setSent(result.data || {});
                // The peer stays filled in: a follow-up usually goes to the same chat.
                setText('');
            }
        } catch (e) {
            console.error('Telegram send error:', e);
            setError(i18n.t('text.unexpectedError'));
        } finally {
            setLoading(false);
        }
    };

    const close = () => {
        if (loading) return;
        closeDialog();
    };

    return (
        <Dialog open={Boolean(openDialog)} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>
                {i18n.t('dialogs.tgSessionSend')}
                {session?.name ? ` — ${session.name}` : ''}
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                    {error && (
                        <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>
                            {error}
                        </Alert>
                    )}
                    {sent && (
                        <Alert severity="success">
                            {i18n.t('text.tgMessageSent', {
                                peer: sent.peerId ?? '',
                                id: sent.tgMessageId ?? ''
                            })}
                        </Alert>
                    )}

                    <TextField
                        label={i18n.t('columns.peer')}
                        value={peer}
                        onChange={(event) => setPeer(event.target.value)}
                        helperText={i18n.t('helperTexts.tgPeerHelper')}
                        disabled={loading}
                        required
                        autoFocus
                        fullWidth
                    />
                    <TextField
                        label={i18n.t('columns.messageText')}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        disabled={loading}
                        required
                        multiline
                        minRows={3}
                        fullWidth
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={close} disabled={loading}>
                    {i18n.t('buttons.close')}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={loading}
                    disabled={!canSend}
                    onClick={send}
                >
                    {i18n.t('buttons.send')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default TgSessionSendDialog;
